import { motion } from "framer-motion";
import {
  ShieldCheck,
  TrendingUp,
  Users,
  Lightbulb,
  Calendar,
  CalendarPlus,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Star,
  Quote,
  Clock,
  PlayCircle,
  GraduationCap,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { RegistrationForm } from "../components/RegistrationForm";
import { HOSTS, VALUE_PROPS, CLASS_TYPES, SCHEDULE, LEARN_ITEMS, TESTIMONIALS, FAQS } from "../data/content";

const ICONS = { ShieldCheck, TrendingUp, Users, Lightbulb };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const icsFor = (type) => (type === "Hot Topic" ? "hot-topic" : "weekly");

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// ---------- Nav ----------
const Nav = () => (
  <header
    data-testid="nav-header"
    className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#FAF8F5]/80 border-b border-[#E7E5E4]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
      <button
        data-testid="nav-logo"
        onClick={() => scrollTo("top")}
        className="flex items-center gap-2.5"
      >
        <span className="w-9 h-9 rounded-xl bg-[#D97757] flex items-center justify-center">
          <GraduationCap className="w-5 h-5 text-white" />
        </span>
        <span className="font-display font-semibold text-[#1C1917] text-lg leading-none">
          The Lending Classroom
        </span>
      </button>
      <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#57534E]">
        <button data-testid="nav-classes" onClick={() => scrollTo("classes")} className="hover:text-[#1C1917] transition-colors">Classes</button>
        <button data-testid="nav-schedule" onClick={() => scrollTo("schedule")} className="hover:text-[#1C1917] transition-colors">Schedule</button>
        <button data-testid="nav-hosts" onClick={() => scrollTo("hosts")} className="hover:text-[#1C1917] transition-colors">Your Hosts</button>
        <button data-testid="nav-faq" onClick={() => scrollTo("faq")} className="hover:text-[#1C1917] transition-colors">FAQ</button>
      </nav>
      <Button
        data-testid="nav-cta"
        onClick={() => scrollTo("register")}
        className="rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
      >
        Register Free
      </Button>
    </div>
  </header>
);

// ---------- Hero ----------
const Hero = () => (
  <section id="top" className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-6 md:px-12 lg:px-24 overflow-hidden">
    <div
      className="absolute top-0 right-0 w-[55%] h-full opacity-[0.18] pointer-events-none hidden lg:block"
      style={{
        backgroundImage:
          "url('https://images.pexels.com/photos/28871767/pexels-photo-28871767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        maskImage: "linear-gradient(to left, black, transparent)",
        WebkitMaskImage: "linear-gradient(to left, black, transparent)",
      }}
    />
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
      <motion.div {...fadeUp} className="lg:col-span-6">
        <div
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 bg-[#F4EBE1] border border-[#E7E5E4] rounded-full px-4 py-1.5 mb-7"
        >
          <Sparkles className="w-4 h-4 text-[#D97757]" />
          <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#A0522D]">
            Free Live Webinar Series
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-[#1C1917] leading-[1.05]">
          Educate your clients.{" "}
          <span className="text-[#D97757]">Earn their trust.</span> Close more deals.
        </h1>
        <p className="mt-6 text-lg text-[#57534E] leading-relaxed max-w-xl">
          Join loan officers George Tabora & Claudia Gomez for free weekly classes and
          hot-topic sessions. Invite your prospects and clients — and let real lending
          education turn fence-sitters into confident buyers.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3">
          {["100% Free to attend", "Live + interactive", "Invite-ready for your clients"].map(
            (t) => (
              <div key={t} className="flex items-center gap-2 text-sm font-medium text-[#1C1917]">
                <CheckCircle2 className="w-4 h-4 text-[#D97757]" />
                {t}
              </div>
            )
          )}
        </div>

        <div
          data-testid="hero-next-class"
          className="mt-7 inline-flex items-center gap-2.5 bg-white border border-[#E7E5E4] rounded-full pl-2 pr-4 py-1.5 shadow-[0_4px_16px_rgb(0,0,0,0.04)]"
        >
          <span className="flex items-center gap-1.5 bg-[#1C1917] text-white text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#5cb85c] animate-pulse" /> Next class
          </span>
          <span className="text-sm font-medium text-[#1C1917]">Tuesday · 6:30 PM ET — The Buyer's Roadmap</span>
        </div>

        <div className="mt-9 flex items-center gap-4">
          <Button
            data-testid="hero-cta"
            onClick={() => scrollTo("register")}
            className="h-12 px-7 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
          >
            Save My Seat <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <button
            data-testid="hero-secondary"
            onClick={() => scrollTo("classes")}
            className="text-sm font-semibold text-[#1C1917] hover:text-[#D97757] transition-colors"
          >
            See what we teach →
          </button>
        </div>

        <div className="mt-10 flex items-center gap-3">
          <div className="flex -space-x-3">
            {HOSTS.map((h) => (
              <img
                key={h.name}
                src={h.image}
                alt={h.name}
                className="w-10 h-10 rounded-full object-cover object-top border-2 border-[#FAF8F5]"
              />
            ))}
          </div>
          <div className="text-sm text-[#57534E]">
            Hosted by your <span className="font-semibold text-[#1C1917]">American Pacific Mortgage</span> loan officers
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
        id="register"
        className="lg:col-span-6 scroll-mt-24"
      >
        <RegistrationForm />
      </motion.div>
    </div>
  </section>
);

// ---------- Value Props ----------
const ValueProps = () => (
  <section id="value" className="py-24 px-6 md:px-12 lg:px-24 bg-white border-y border-[#E7E5E4]">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
        <div className="lg:col-span-7">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
            Why agents invite their clients
          </span>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
            The agents who educate are the agents people trust.
          </h2>
          <p className="text-[#57534E] leading-relaxed mt-4 max-w-2xl">
            When you bring your sphere into a free, no-pressure classroom, you stop being
            "just another agent" — you become the trusted guide who helps people understand
            their options and take the next step.
          </p>
        </div>
        <div className="lg:col-span-5">
          <img
            data-testid="value-image"
            src="https://images.pexels.com/photos/7937321/pexels-photo-7937321.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Loan officer educating clients"
            className="w-full h-72 object-cover rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {VALUE_PROPS.map((v, i) => {
          const Icon = ICONS[v.icon];
          return (
            <motion.div
              key={v.title}
              {...fadeUp}
              transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.08 }}
              data-testid={`value-card-${i}`}
              className="bg-[#FAF8F5] rounded-[2rem] p-8 border border-[#E7E5E4] hover:border-[#D97757]/40 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#F4EBE1] flex items-center justify-center mb-5">
                <Icon className="w-6 h-6 text-[#D97757]" />
              </div>
              <h3 className="font-display text-xl font-medium text-[#1C1917] mb-2">{v.title}</h3>
              <p className="text-[#57534E] leading-relaxed">{v.body}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// ---------- Classes (Bento) ----------
const Classes = () => (
  <section id="classes" className="py-24 px-6 md:px-12 lg:px-24 scroll-mt-16">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeUp} className="max-w-2xl mb-14">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          Two kinds of classes
        </span>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
          Consistent learning, plus timely deep-dives.
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
        {/* Weekly - large */}
        <motion.div
          {...fadeUp}
          data-testid="classes-weekly"
          className="md:col-span-7 bg-[#1C1917] text-white rounded-[2rem] p-9 md:p-12 relative overflow-hidden"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8A87C]">
            {CLASS_TYPES[0].tag}
          </span>
          <h3 className="font-display text-3xl font-medium mt-3 mb-4">{CLASS_TYPES[0].title}</h3>
          <p className="text-[#D6CFC7] leading-relaxed max-w-md">{CLASS_TYPES[0].body}</p>
          <ul className="mt-7 space-y-3">
            {CLASS_TYPES[0].bullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-[#F4EBE1]">
                <CheckCircle2 className="w-5 h-5 text-[#E8A87C]" /> {b}
              </li>
            ))}
          </ul>
          <Calendar className="absolute -bottom-8 -right-8 w-44 h-44 text-white/[0.05]" />
        </motion.div>

        {/* Hot Topic - small */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          data-testid="classes-hot-topic"
          className="md:col-span-5 bg-[#F4EBE1] rounded-[2rem] p-9 md:p-12 border border-[#E7E5E4] relative overflow-hidden"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
            {CLASS_TYPES[1].tag}
          </span>
          <h3 className="font-display text-3xl font-medium text-[#1C1917] mt-3 mb-4">
            {CLASS_TYPES[1].title}
          </h3>
          <p className="text-[#57534E] leading-relaxed">{CLASS_TYPES[1].body}</p>
          <ul className="mt-7 space-y-3">
            {CLASS_TYPES[1].bullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-[#1C1917]">
                <CheckCircle2 className="w-5 h-5 text-[#D97757]" /> {b}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      <motion.div
        {...fadeUp}
        className="mt-6 lg:mt-8 bg-white rounded-[2rem] p-8 md:p-10 border border-[#E7E5E4] flex flex-col md:flex-row items-center gap-6 justify-between"
      >
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-[#F4EBE1] flex items-center justify-center shrink-0">
            <Star className="w-7 h-7 text-[#D97757]" />
          </div>
          <div>
            <h4 className="font-display text-lg font-medium text-[#1C1917]">
              Plus: invited guest speakers
            </h4>
            <p className="text-[#57534E] mt-1">
              We regularly bring in industry experts to share insights your clients won't hear anywhere else.
            </p>
          </div>
        </div>
        <Button
          data-testid="classes-cta"
          onClick={() => scrollTo("register")}
          className="rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold h-11 px-6 transition-all duration-300 hover:-translate-y-0.5 shrink-0"
        >
          Register for the next class
        </Button>
      </motion.div>
    </div>
  </section>
);

// ---------- Schedule ----------
const Schedule = () => (
  <section id="schedule" className="py-24 px-6 md:px-12 lg:px-24 bg-white border-y border-[#E7E5E4] scroll-mt-16">
    <div className="max-w-5xl mx-auto">
      <motion.div {...fadeUp} className="mb-12">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          When we meet
        </span>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
          A rhythm your clients can count on.
        </h2>
      </motion.div>

      <div data-testid="schedule-list" className="divide-y divide-[#E7E5E4] border-y border-[#E7E5E4]">
        {SCHEDULE.map((s, i) => (
          <motion.div
            key={i}
            {...fadeUp}
            transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.06 }}
            className="flex flex-col sm:flex-row sm:items-center justify-between py-6 gap-3 group"
          >
            <div className="flex items-center gap-5">
              <div className="text-center min-w-[110px]">
                <div className="font-display text-base font-semibold text-[#1C1917]">{s.day}</div>
                <div className="text-sm text-[#57534E]">{s.time}</div>
              </div>
              <div className="h-10 w-px bg-[#E7E5E4] hidden sm:block" />
              <div className="font-medium text-[#1C1917] group-hover:text-[#D97757] transition-colors">
                {s.title}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full w-fit ${
                  s.type === "Weekly"
                    ? "bg-[#1C1917] text-white"
                    : s.type === "Hot Topic"
                    ? "bg-[#F4EBE1] text-[#A0522D]"
                    : "bg-[#D97757] text-white"
                }`}
              >
                {s.type}
              </span>
              <a
                data-testid={`schedule-ics-${i}`}
                href={`${API}/calendar/${icsFor(s.type)}.ics`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#57534E] hover:text-[#D97757] transition-colors"
              >
                <CalendarPlus className="w-4 h-4" /> Add
              </a>
            </div>
          </motion.div>
        ))}
      </div>
      <p className="text-sm text-[#A8A29E] mt-6">
        Times shown in Eastern Time. Register once to get reminders for every session.
      </p>
    </div>
  </section>
);

// ---------- Hosts ----------
const Hosts = () => (
  <section id="hosts" className="py-24 px-6 md:px-12 lg:px-24 scroll-mt-16">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeUp} className="max-w-2xl mb-14">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          Your hosts
        </span>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
          Real loan officers. Real answers.
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {HOSTS.map((h, i) => (
          <motion.div
            key={h.name}
            {...fadeUp}
            transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.1 }}
            data-testid={`bios-card-${i}`}
            className="bg-[#F4EBE1] rounded-[2rem] p-8 border border-[#E7E5E4] flex flex-col sm:flex-row gap-7"
          >
            <img
              src={h.image}
              alt={h.name}
              className="w-28 h-28 rounded-2xl object-cover object-top shrink-0 shadow-[0_8px_30px_rgb(0,0,0,0.10)]"
            />
            <div>
              <h3 className="font-display text-2xl font-medium text-[#1C1917]">{h.name}</h3>
              <p className="text-sm font-semibold text-[#D97757] mt-1">{h.title}</p>
              <p className="text-xs text-[#A8A29E] mt-0.5">{h.nmls}</p>
              <p className="text-[#57534E] leading-relaxed mt-3">{h.bio}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- What You'll Learn ----------
const Learn = () => (
  <section id="learn" className="py-24 px-6 md:px-12 lg:px-24 bg-white border-y border-[#E7E5E4] scroll-mt-16">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
      <motion.div {...fadeUp} className="lg:col-span-5">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          What you'll learn
        </span>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
          Real takeaways your clients can actually use.
        </h2>
        <p className="text-[#57534E] leading-relaxed mt-4">
          Every session is built to give agents and buyers concrete, plain-English answers — the
          kind of knowledge that turns "maybe someday" into "let's get pre-approved."
        </p>
        <Button
          data-testid="learn-cta"
          onClick={() => scrollTo("register")}
          className="mt-7 h-12 px-7 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
        >
          Reserve My Seat <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </motion.div>

      <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {LEARN_ITEMS.map((item, i) => (
          <motion.div
            key={i}
            {...fadeUp}
            transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.05 }}
            data-testid={`learn-item-${i}`}
            className="flex items-start gap-3 bg-[#FAF8F5] rounded-2xl p-5 border border-[#E7E5E4]"
          >
            <CheckCircle2 className="w-5 h-5 text-[#D97757] mt-0.5 shrink-0" />
            <span className="text-[#1C1917] leading-relaxed">{item}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Replay / Lead Magnet ----------
const ReplayOffer = () => (
  <section className="py-16 px-6 md:px-12 lg:px-24">
    <motion.div
      {...fadeUp}
      data-testid="replay-offer"
      className="max-w-5xl mx-auto bg-[#F4EBE1] rounded-[2rem] p-8 md:p-12 border border-[#E7E5E4] flex flex-col md:flex-row items-center gap-7 justify-between"
    >
      <div className="flex items-center gap-5">
        <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-[0_4px_16px_rgb(0,0,0,0.06)]">
          <PlayCircle className="w-7 h-7 text-[#D97757]" />
        </div>
        <div>
          <h3 className="font-display text-xl md:text-2xl font-medium text-[#1C1917]">
            Can't make it live? Get the recording.
          </h3>
          <p className="text-[#57534E] mt-1.5 max-w-lg">
            Register anyway and we'll send you the full class replay — so you (and the clients you
            invite) never miss the lesson.
          </p>
        </div>
      </div>
      <Button
        data-testid="replay-cta"
        onClick={() => scrollTo("register")}
        className="rounded-xl bg-[#1C1917] hover:bg-black text-white font-semibold h-12 px-7 transition-all duration-300 hover:-translate-y-0.5 shrink-0"
      >
        Send Me the Replay
      </Button>
    </motion.div>
  </section>
);

// ---------- Testimonials ----------
const Testimonials = () => (
  <section id="testimonials" className="py-24 px-6 md:px-12 lg:px-24 scroll-mt-16">
    <div className="max-w-7xl mx-auto">
      <motion.div {...fadeUp} className="max-w-2xl mb-14">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          What people say
        </span>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
          Agents win deals. Clients gain confidence.
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            {...fadeUp}
            transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.1 }}
            data-testid={`testimonial-${i}`}
            className="bg-white rounded-[2rem] p-8 border border-[#E7E5E4] flex flex-col hover:-translate-y-1 transition-all duration-300"
          >
            <Quote className="w-8 h-8 text-[#D97757] mb-4" />
            <div className="flex gap-0.5 mb-4">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} className="w-4 h-4 fill-[#D97757] text-[#D97757]" />
              ))}
            </div>
            <p className="text-[#1C1917] leading-relaxed flex-1">"{t.quote}"</p>
            <div className="mt-6 pt-5 border-t border-[#E7E5E4]">
              <div className="font-display font-semibold text-[#1C1917]">{t.name}</div>
              <div className="text-sm text-[#D97757]">{t.role}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- FAQ ----------
const FAQ = () => (
  <section id="faq" className="py-24 px-6 md:px-12 lg:px-24 bg-white border-t border-[#E7E5E4] scroll-mt-16">
    <div className="max-w-3xl mx-auto">
      <motion.div {...fadeUp} className="mb-12 text-center">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          Questions
        </span>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1C1917] leading-snug mt-3">
          Everything you need to know.
        </h2>
      </motion.div>

      <motion.div {...fadeUp}>
        <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="bg-[#FAF8F5] border border-[#E7E5E4] rounded-2xl px-6 overflow-hidden"
            >
              <AccordionTrigger className="text-left font-display font-medium text-[#1C1917] hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#57534E] leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </div>
  </section>
);

// ---------- Final CTA ----------
const FinalCTA = () => (
  <section className="py-24 px-6 md:px-12 lg:px-24">
    <motion.div
      {...fadeUp}
      className="max-w-5xl mx-auto bg-[#1C1917] rounded-[2.5rem] p-10 md:p-16 text-center relative overflow-hidden"
    >
      <GraduationCap className="absolute -top-6 -left-6 w-40 h-40 text-white/[0.04]" />
      <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8A87C]">
        Ready when you are
      </span>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight mt-4 max-w-2xl mx-auto">
        Become the agent who teaches — and watch the trust follow.
      </h2>
      <p className="text-[#D6CFC7] leading-relaxed mt-5 max-w-xl mx-auto">
        Reserve your seat now and bring a client along. It's free, it's friendly, and it just
        might be the easiest deal-builder in your toolkit.
      </p>
      <Button
        data-testid="final-cta"
        onClick={() => scrollTo("register")}
        className="mt-8 h-13 px-8 py-6 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
      >
        Register Free <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </motion.div>
  </section>
);

// ---------- Footer ----------
const Footer = () => (
  <footer data-testid="footer" className="bg-[#1C1917] px-6 md:px-12 lg:px-24 py-16 pb-28 md:pb-16">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between gap-10 pb-10 border-b border-white/10">
        <div className="max-w-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-9 h-9 rounded-xl bg-[#D97757] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </span>
            <span className="font-display font-semibold text-white text-lg">
              The Lending Classroom
            </span>
          </div>
          <p className="text-sm text-[#A8A29E] leading-relaxed">
            Free lending education hosted by George Tabora & Claudia Gomez, Loan Officers at
            American Pacific Mortgage.
          </p>
        </div>
        <div className="flex gap-16">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8A87C] mb-4">Explore</div>
            <ul className="space-y-2.5 text-sm text-[#A8A29E]">
              <li><button onClick={() => scrollTo("classes")} className="hover:text-white transition-colors">Classes</button></li>
              <li><button onClick={() => scrollTo("schedule")} className="hover:text-white transition-colors">Schedule</button></li>
              <li><button onClick={() => scrollTo("hosts")} className="hover:text-white transition-colors">Your Hosts</button></li>
              <li><button onClick={() => scrollTo("faq")} className="hover:text-white transition-colors">FAQ</button></li>
            </ul>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8A87C] mb-4">Join</div>
            <ul className="space-y-2.5 text-sm text-[#A8A29E]">
              <li><button onClick={() => scrollTo("register")} className="hover:text-white transition-colors">Register Free</button></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="pt-8 text-xs text-[#78716C] leading-relaxed space-y-3">
        <p>
          American Pacific Mortgage Corporation | Company NMLS #1850. Equal Housing Opportunity.
          Branch: 9711 Washingtonian Blvd, Suite 550, Office 547 &amp; 550, Gaithersburg, MD 20878.
          This webinar series is for educational purposes only and does not constitute a
          commitment to lend or financial advice. Loan products and program availability are
          subject to change and borrower qualification. All loans subject to credit approval.
          George Tabora — NMLS #2049966. Claudia Gomez — NMLS #2298266.
        </p>
        <p>© {new Date().getFullYear()} The Lending Classroom. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <div className="grain-overlay" />
      <Nav />
      <main className="relative">
        <Hero />
        <ValueProps />
        <Classes />
        <Learn />
        <Schedule />
        <Hosts />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}

function MobileCTA() {
  return (
    <div
      data-testid="mobile-cta-bar"
      className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#FAF8F5]/90 backdrop-blur-xl border-t border-[#E7E5E4] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
    >
      <Button
        data-testid="mobile-cta-button"
        onClick={() => scrollTo("register")}
        className="w-full h-12 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold text-base shadow-lg"
      >
        Register Free <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
}
